import {sleep, useMock} from "../utils/helpers";
import * as CryptoJS from 'crypto-js';
import http from '../plugins/http';

import {mapGetters} from 'vuex';

import config from '../config';

const authUri = config.services.auth;
const ratingUri = config.services.rating;
const mobileUrl = config.services.mobile;
const AUTH_STUB_STORAGE_KEY = 'economix.auth.stub';

const defaultKycSteps = {
  step1: {
    firstName: '',
    middleName: '',
    lastName: '',
    gender: '',
    dateBirth: null,
  },
  step2: {
    countryOfResidents: '',
    address: '',
    city: '',
    zip: '',
    phone: '',
  },
  step3: {
    idIssuingCountry: '',
    idNumber: '',
    idExpirationDate: null,
  },
};

const defaultAuthStub = {
  user: {
    id: 1,
    name: 'Demo',
    surname: 'User',
    email: 'demo@economix.ai',
    kycStatus: 'Tier0',
    amountLimit: 15000,
    lastLoginDate: new Date().toISOString(),
    kyc: {
      status: 'draft',
      ...defaultKycSteps,
    },
  },
  kyc: {
    ...defaultKycSteps,
  },
};

class Auth {

  constructor() {
    this.useMock = useMock('auth');
  }

  getAuthStub() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        user: { ...defaultAuthStub.user, kyc: { ...defaultAuthStub.user.kyc } },
        kyc: { ...defaultAuthStub.kyc },
      };
    }

    try {
      const raw = window.localStorage.getItem(AUTH_STUB_STORAGE_KEY);
      if (!raw) {
        return {
          user: { ...defaultAuthStub.user, kyc: { ...defaultAuthStub.user.kyc } },
          kyc: { ...defaultAuthStub.kyc },
        };
      }

      const parsed = JSON.parse(raw);
      return {
        user: {
          ...defaultAuthStub.user,
          ...(parsed.user || {}),
          kyc: {
            ...defaultAuthStub.user.kyc,
            ...((parsed.user && parsed.user.kyc) || {}),
          },
        },
        kyc: {
          ...defaultAuthStub.kyc,
          ...(parsed.kyc || {}),
        },
      };
    } catch (e) {
      return {
        user: { ...defaultAuthStub.user, kyc: { ...defaultAuthStub.user.kyc } },
        kyc: { ...defaultAuthStub.kyc },
      };
    }
  }

  setAuthStub(stub) {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }

    window.localStorage.setItem(AUTH_STUB_STORAGE_KEY, JSON.stringify(stub));
  }

  updateAuthStub(step, data) {
    const stub = this.getAuthStub();

    stub.kyc[step] = {
      ...stub.kyc[step],
      ...data,
    };

    stub.user = {
      ...stub.user,
      name: stub.kyc.step1.firstName || stub.user.name,
      surname: stub.kyc.step1.lastName || stub.user.surname,
      kyc: {
        ...stub.user.kyc,
        ...stub.kyc,
      },
    };

    this.setAuthStub(stub);
    return stub;
  }

  shouldUseStub(error) {
    return this.useMock || !error || !error.response;
  }

  async login({login, password, clientId, clientSecret}) {
    let secret = "VmVyeVNlY3JldEtleTE=";
    const sign = CryptoJS.HmacSHA1(login + password + clientId + clientSecret, secret);

    const params = { username: login, password: password };

    const headers = {
      'clientId': clientId,
      'sign': sign
    };

    const data = await http.post(`${authUri}/api/v1/login`, params, { headers });

    return data.data;
  }

  async refreshToken({clientId, clientSecret, refreshToken}) {
    const headers = {
      'clientId': clientId,
      'refreshToken': refreshToken
    };

    const data = await http.get(`${authUri}/api/v1/refreshtoken`, {headers});

    console.log(data.data);

    return data.data;
  }

  async fetchUserData() {
    if(this.useMock) {
      return new Promise(async resolve => {
        await sleep(500);
        resolve((await import('../../mock/auth.js')).default.user);
      });
    }

    const {data} = await http.get(`${ratingUri}/api/v1/user/info`);

    return data.data;
  }

  async fetchModerationGroup(assetId, token) {
    const headers = {
      'Authorization': token,
    };

    const {data} = await http.get(`${ratingUri}/api/v1/user/moderation/group/current/${assetId}`, { headers });

    return data.data;
  }

  async fetchModeration(moderationId) {
    const {data} = await http.get(`${ratingUri}/api/v1/user/moderation/${moderationId}`);

    return data.data;
  }

  async getUserActivityList(filter) {

    const params = {
      limit: filter.limit || 10,
      sort: filter.sort || null,
      page: filter.page || 1,
    };

    const {data} = await http.get(`${ratingUri}/api/v1/user/activity/list`, { params });

    return data;
  }

  async changeUserData(userData) {
    await http.post(`${ratingUri}/api/v1/user/info/change`, userData);
  }

  async saveKYCStep({data, step}) {
    try {
      await http.post(`${authUri}/api/v1/user/kyc`, data, { params: { step: `step${step}` } });
    } catch (error) {
      if (!this.shouldUseStub(error)) {
        throw error;
      }

      this.updateAuthStub(`step${step}`, data);
    }
  }

  async getKYCStep(step) {
    try {
      const {data} = await http.get(`${authUri}/api/v1/user/kyc`, step !== null ? { params: { step } } : {});
      return data;
    } catch (error) {
      if (!this.shouldUseStub(error)) {
        throw error;
      }

      const stub = this.getAuthStub();
      return stub.kyc[`step${step}`] || {};
    }
  }

  async getKYCUser() {
    try {
      const {data} = await http.get(`${authUri}/api/v1/user`);
      return data;
    } catch (error) {
      if (!this.shouldUseStub(error)) {
        throw error;
      }

      return this.getAuthStub().user;
    }
  }

  async sendVerifyCode(phone, userId) {
    http.get(`${mobileUrl}/api/v1/mobauth/verificationPhone`, { params: { phone, userId }});
  }
}

export default new Auth();



// WEBPACK FOOTER //
// ./src/services/auth.js